// src/components/home/ProductVideo.js
// Vidéo de présentation de la landing, auto-hébergée (pas d'embed tiers, donc
// pas de cookies) :
//  - aperçu en boucle, muet, chargé à l'approche de la section et lu seulement
//    quand il est à l'écran (rien du tout en prefers-reduced-motion : affiche seule) ;
//  - bouton « Regarder avec le son » qui ouvre la version complète dans une modale.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Play, X } from 'lucide-react';
import styles from './ProductVideo.module.css';
import { useAnimationGate, useInViewport } from './useDemoAnimation';

// Les fichiers sont servis avec un cache navigateur de 7 jours (staticwebapp.config.json) :
// chaque nouvelle version de la vidéo doit porter un nouveau suffixe (-v3…), sinon les
// visiteurs continuent de voir l'ancienne.
const LOOP_SRC   = '/videos/cvtoskill-demo-loop-v2.mp4'; // 720p, sans piste son
const FULL_SRC   = '/videos/cvtoskill-demo-v2.mp4';      // 1080p, avec le son
const POSTER_SRC = '/videos/cvtoskill-demo-poster-v2.jpg';
const DURATION_LABEL = '2 min 05';

/* ═══════════════════════════════════════════════════════════
   MODALE — version complète avec le son
   ═══════════════════════════════════════════════════════════ */

const VideoModal = ({ onClose }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [onClose]);

  return createPortal(
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Vidéo de présentation de CVtoSkill"
      onClick={onClose}
    >
      <div className={styles.dialog} onClick={(event) => event.stopPropagation()}>
        <button
          ref={closeRef}
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Fermer la vidéo"
        >
          <X size={20} strokeWidth={2.25} />
        </button>
        <video
          className={styles.fullVideo}
          src={FULL_SRC}
          poster={POSTER_SRC}
          controls
          autoPlay
          playsInline
          preload="auto"
        />
      </div>
    </div>,
    document.body
  );
};

/* ═══════════════════════════════════════════════════════════
   APERÇU EN BOUCLE
   ═══════════════════════════════════════════════════════════ */

const ProductVideo = () => {
  const frameRef = useRef(null);
  const loopRef  = useRef(null);
  const { running, reduced } = useAnimationGate(frameRef);
  const nearView = useInViewport(frameRef, '300px');

  // La source n'est attachée qu'à l'approche de la section : rien n'est
  // téléchargé tant que le visiteur reste en haut de page.
  const [attached, setAttached] = useState(false);
  const [isOpen, setIsOpen]     = useState(false);

  useEffect(() => {
    if (nearView && !reduced) setAttached(true);
  }, [nearView, reduced]);

  useEffect(() => {
    const video = loopRef.current;
    if (!video || !attached) return;
    if (running && !isOpen) {
      video.muted = true; // requis par les navigateurs pour la lecture automatique
      const playing = video.play();
      if (playing && typeof playing.catch === 'function') playing.catch(() => {});
    } else {
      video.pause();
    }
  }, [running, attached, isOpen]);

  const open  = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <div ref={frameRef} className={styles.frame}>
        <video
          ref={loopRef}
          className={styles.loop}
          src={attached ? LOOP_SRC : undefined}
          poster={POSTER_SRC}
          muted
          loop
          playsInline
          preload="none"
          disablePictureInPicture
          aria-hidden="true"
          tabIndex={-1}
        />
        <div className={styles.shade} aria-hidden="true" />

        <button type="button" className={styles.play} onClick={open} aria-haspopup="dialog">
          <span className={styles.playIcon}>
            <Play size={22} strokeWidth={2.25} fill="currentColor" />
          </span>
          <span className={styles.playLabel}>
            Regarder avec le son
            <small>{DURATION_LABEL}</small>
          </span>
        </button>
      </div>

      {isOpen && <VideoModal onClose={close} />}
    </>
  );
};

export default ProductVideo;
